<template>
  <Teleport to="body">
    <div class="sheet-backdrop" @click.self="$emit('close')">
      <div class="sheet share" role="dialog" aria-label="Solve together">
        <button class="sheet__close" aria-label="Close" @click="$emit('close')">✕</button>
        <h2>{{ roomMeta?.groupName || 'Solve together' }}</h2>

        <template v-if="roomMeta">
          <p v-if="roomMeta.groupId && puzzle?.giant" class="muted">This is {{ roomMeta.groupName }}’s room for the {{ puzzle.season }} Giant, all season long. Its code brings anyone straight here.</p>
          <p v-else-if="roomMeta.groupId" class="muted">This group's code is for good: every day it opens that day's puzzle here together, with everyone in their own colour.</p>
          <p v-else class="muted">Anyone with this link lands in the same grid, on their own phone. Letters show up live, in each person's colour.</p>
          <button class="share__code" title="Copy join link" @click="copy">{{ roomMeta.code }}</button>
          <button class="share__link" title="Copy join link" @click="copy">{{ link }}</button>
          <button class="primary" @click="sendLink">{{ copied ? 'Link copied' : (touch ? 'Send join link' : 'Copy join link') }}</button>
          <ul class="share__members">
            <li v-for="m in members" :key="m.uid"><i class="dot" :style="{ background: m.color }" /> {{ m.name }}<span v-if="m.uid === myUid" class="muted"> (you)</span></li>
          </ul>
          <form v-if="!roomMeta.groupId" class="share__keep" :class="{ 'is-asked': keep }" @submit.prevent="keepGroup">
            <p class="share__keep-title">Keep this group</p>
            <p class="muted">Same people, same colours, same code, every day: name it and it sits on everyone's home screen, one tap from that day's puzzle.</p>
            <div class="share__keep-row">
              <input ref="groupName" v-model="groupName" placeholder="Group name, like Family" maxlength="30" autocomplete="off">
              <button type="submit" class="secondary" :disabled="!groupName.trim() || keeping">Keep</button>
            </div>
            <p v-if="roomError" class="notice notice--bad">{{ roomError }}</p>
          </form>
          <button class="secondary" @click="leave">Back to solving alone</button>
        </template>

        <template v-else-if="creating">
          <p class="muted">Setting up&hellip;</p>
        </template>

        <template v-else>
          <p class="muted">Share this puzzle with a friend and fill it in at the same time. Your letters so far come with you.</p>
          <button class="primary" @click="share">Invite a friend</button>
          <p v-if="roomError" class="notice notice--bad">{{ roomError }}</p>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { joinLink } from '@/store/rooms';

export default {
  name: 'ShareSheet',
  props: {
    // Opened from the menu's "Keep this group": go straight to the name.
    keep: { type: Boolean, default: false },
  },
  emits: ['close'],
  data() {
    return {
      groupName: '',
      keeping: false,
      creating: false,
      copied: false,
      // Phones get the share menu; computers just copy (Chrome on a Mac has
      // navigator.share too, but its menu is no way to hand someone a link).
      touch: typeof window !== 'undefined' && Boolean(window.matchMedia?.('(pointer: coarse)').matches),
    };
  },
  computed: {
    ...mapState(['roomMeta', 'roomError', 'puzzle']),
    ...mapGetters(['members', 'myUid']),
    link() { return this.roomMeta ? joinLink(this.roomMeta.code) : ''; },
    shareText() {
      const tail = `${this.link} (or enter code ${this.roomMeta.code})`;
      const { puzzle } = this;
      if (puzzle?.giant) return `Solve the ${puzzle.season} Giant crossword with me: ${tail}`;
      return this.roomMeta.groupId
        ? `Join ${this.roomMeta.groupName} for the daily crossword: ${tail}`
        : `Solve today's crossword with me: ${tail}`;
    },
  },
  mounted() {
    if (this.keep) this.$nextTick(() => this.$refs.groupName?.focus());
  },
  methods: {
    async keepGroup() {
      this.keeping = true;
      await this.$store.dispatch('keepRoomAsGroup', this.groupName);
      this.keeping = false;
    },
    async share() {
      this.creating = true;
      await this.$store.dispatch('shareRoom');
      this.creating = false;
    },
    async copy() {
      try {
        await navigator.clipboard.writeText(this.link);
        this.copied = true;
      } catch { /* no clipboard */ }
    },
    async sendLink() {
      if (!this.touch || !navigator.share) { await this.copy(); return; }
      try {
        await navigator.share({
          title: 'Solve this crossword with me',
          text: this.shareText,
        });
      } catch { /* dismissed */ }
    },
    leave() {
      this.$store.dispatch('leaveRoom');
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.share { text-align: center; }
h2 { margin: 0 0 0.4rem; }
.share__code { display: block; margin: 0.6rem auto 0; padding: 0; background: none; border: 0; cursor: pointer;
  font-size: 2.2rem; font-weight: 800; letter-spacing: 0.25em; color: var(--accent); }
.share__link { display: block; margin: 0.2rem auto 0.8rem; padding: 0; background: none; border: 0; cursor: pointer;
  font-size: 0.85rem; color: inherit; opacity: 0.75; word-break: break-all; text-decoration: underline; }
.share__members { list-style: none; margin: 0.8rem 0; padding: 0; text-align: left;
  li { padding: 0.2rem 0; display: flex; align-items: center; gap: 0.5rem; } }
.share__keep { margin: 0.4rem 0 0.8rem; padding: 0.7rem; border: 1px solid var(--edge); border-radius: var(--radius); text-align: left;
  &.is-asked { border-color: var(--accent-edge); }
  .muted { margin: 0.2rem 0 0.5rem; font-size: 0.85rem; } }
.share__keep-title { margin: 0; font-weight: 700; }
.share__keep-row { display: flex; gap: 0.5rem;
  input { flex: 1; min-width: 0; }
  .secondary { flex: 0 0 auto; width: auto; padding: 0 1rem; } }
.dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; }
</style>
